import { type GetAssetsBrandValidationMetadataQuery } from './gql/queries/generated/GetAssetsBrandValidationMetadata';

export const ALLOWED_BRAND_VALIDATION_MIME_TYPES = [
  'image/gif',
  'image/jpeg',
  'image/png',
  'image/svg+xml',
  'image/webp',
] as const;

export type AllowedBrandValidationMimeType =
  (typeof ALLOWED_BRAND_VALIDATION_MIME_TYPES)[number];

const ALLOWED_MIME_TYPE_SET = new Set<string>(
  ALLOWED_BRAND_VALIDATION_MIME_TYPES
);

export type BrandValidationAssetInput = {
  id: string;
  name?: string | null;
  mimeType?: string | null;
};

export type BrandValidationFailure =
  | { id: string; name: string | null; reason: 'missing_metadata' }
  | {
      id: string;
      name: string | null;
      mimeType: string;
      reason: 'unsupported_format';
    };

export type ValidationErrorKind = BrandValidationFailure['reason'];

export type BrandValidationResult = {
  validAssetIds: string[];
  failedAssets: BrandValidationFailure[];
};

export function isAllowedBrandValidationMimeType(mimeType: string): boolean {
  return ALLOWED_MIME_TYPE_SET.has(mimeType.toLowerCase());
}

export function toBrandValidationInputs(
  assets: NonNullable<
    NonNullable<GetAssetsBrandValidationMetadataQuery['account']>['assets']
  >['nodes']
): BrandValidationAssetInput[] {
  return assets
    .filter((asset) => asset !== null)
    .map((asset) => ({
      id: asset.id,
      name: asset.name,
      mimeType:
        asset.__typename === 'ImageAsset'
          ? asset.media?.mimeType ?? null
          : null,
    }));
}

export function validateBrandValidationAssets(
  assets: BrandValidationAssetInput[]
): BrandValidationResult {
  const validAssetIds: string[] = [];
  const failedAssets: BrandValidationFailure[] = [];

  for (const asset of assets) {
    const mimeType = asset.mimeType ?? null;
    if (mimeType == null || mimeType === '') {
      failedAssets.push({
        id: asset.id,
        name: asset.name ?? null,
        reason: 'missing_metadata',
      });
    } else if (!isAllowedBrandValidationMimeType(mimeType)) {
      failedAssets.push({
        id: asset.id,
        name: asset.name ?? null,
        mimeType,
        reason: 'unsupported_format',
      });
    } else {
      validAssetIds.push(asset.id);
    }
  }

  return { validAssetIds, failedAssets };
}
